'use client'

import { hyperEVM } from '@/config/chain/constants'
import { useAccountLiquidity } from '@/domain/euler/positions/useAccountLiquidity'
import { ELendPosition } from '@/domain/euler/positions/useELendPositions'
import { useEnabledCollateralVaults } from '@/domain/euler/positions/useEnabledCollateralVaults'
import { getSubAccountAddress } from '@/domain/euler/shared/subAccounts'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { useBorrowPairs } from '@/features/eborrow/logic/useBorrowPairs'
import { useVaultClassifier } from '@/features/card/contexts/VaultClassifierContext'
import { DialogPanel } from '@/features/dialogs/common/components/DialogPanel'
import { MultiPanelDialog } from '@/features/dialogs/common/components/MultiPanelDialog'
import { getTokenImage } from '@/ui/assets/tokens'
import { Button } from '@/ui/atoms/button/Button'
import { Dialog, DialogContent, DialogTitle } from '@/ui/atoms/dialog/Dialog'
import { Panel } from '@/ui/atoms/panel/Panel'
import { Tooltip, TooltipContentShort, TooltipTrigger } from '@/ui/atoms/tooltip/Tooltip'
import { Typography } from '@/ui/atoms/typography/Typography'
import { cn } from '@/ui/utils/style'
import { AlertTriangle, Check, ChevronLeft, ChevronRight, Info, Loader2, Plus } from 'lucide-react'
import Image from 'next/image'
import { Address } from 'viem'
import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import { useAccount } from 'wagmi'
import { type BorrowTokenOption, DEFAULT_MAX_CARDS_FALLBACK, type EulerVaultInfo } from '../constants'
import { type MergedPosition } from '../hooks/useAllEulerPositions'
import { useCardAutoSelection } from '../hooks/useCardAutoSelection'
import { useCreateCard } from '../hooks/useCreateCard'
import { useHypurrFiCardData } from '../hooks/useHypurrFiCardData'
import { useLtvWeightedCollateral } from '../hooks/useLtvWeightedCollateral'
import { useSolverOptions } from '../hooks/useSolverOptions'
import { useUpdateCardLabel } from '../hooks/useUpdateCardLabel'
import {
  getRequiredConfiguredSelectionError,
  getSpendCollateralEmptyStateMessage,
  getSpendCollateralSupportedIconDisplay,
} from './cardConfiguration.utils'
import {
  type AddCardWizardOption,
  getAddCardWizardOptions,
  getInitialWizardEulerVault,
  getWizardBorrowTokenOptions,
  getWizardEulerPositions,
  getWizardSolverModeLabel,
  getWizardTokenFieldMeta,
} from './addCardWizard.utils'

type WizardStep = 'solver' | 'config' | 'name' | 'review'
const STEPS: WizardStep[] = ['solver', 'config', 'name', 'review']
const LABEL_MAX_LENGTH = 50
type EulerSpendWizardAsset = ELendPosition & {
  spendingPowerUSD: { toNumber: () => number; minus: (other: any) => any }
}
type WizardEulerPositionPreview = {
  subAccountId: number
  spendingPower?: number
  savingsUSD: number
  hasEnabledCollateral: boolean
  hasSavings: boolean
  savingsOnly: boolean
  isLoading: boolean
}

interface AddCardWizardProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  currentCardCount: number
  maxCards?: number
  onCardCreated?: (opts: { needsFunding: boolean; cardId?: string }) => void
}

export function AddCardWizard({ open, onOpenChange, currentCardCount, maxCards, onCardCreated }: AddCardWizardProps) {
  const { address } = useAccount()
  const {
    hypurrFiBorrowTokens,
    hypurrFiWithdrawTokens,
    hypurrFiATokenByBaseToken,
    primeEulerVaults,
    primeEulerSpendVaults,
    yieldEulerVaults,
    yieldEulerSpendVaults,
    isLoading: isSolverOptionsLoading,
  } = useSolverOptions()
  const { createCard, isPending: isCreating, error: createError, reset: resetCreate } = useCreateCard()
  const { updateLabel, isPending: isLabeling } = useUpdateCardLabel()

  const [step, setStep] = useState<WizardStep>('solver')
  const [solverModeSelection, setSolverModeSelection] = useState<'borrow' | 'spend'>('borrow')
  const [selectedSolver, setSelectedSolver] = useState<AddCardWizardOption | null>(null)
  const [selectedCluster, setSelectedCluster] = useState<'prime' | 'yield' | null>(null)
  const [selectedVault, setSelectedVault] = useState<EulerVaultInfo | null>(null)
  const [selectedBorrowToken, setSelectedBorrowToken] = useState<BorrowTokenOption | null>(null)
  const [selectedSpendAsset, setSelectedSpendAsset] = useState<EulerSpendWizardAsset | null>(null)
  const [subAccountId, setSubAccountId] = useState(0)
  const [isNewPosition, setIsNewPosition] = useState(false)
  const [cardLabel, setCardLabel] = useState('')
  const [isSuccess, setIsSuccess] = useState(false)
  const [labelError, setLabelError] = useState<string | null>(null)
  const [configError, setConfigError] = useState<string | null>(null)
  const [createdCardId, setCreatedCardId] = useState<string | null>(null)
  const [solverPreviewSelections, setSolverPreviewSelections] = useState<Record<string, number>>({})

  const effectiveMaxCards = maxCards ?? DEFAULT_MAX_CARDS_FALLBACK
  const isMaxed = currentCardCount >= effectiveMaxCards
  const isEuler = selectedSolver?.market === 'euler'
  const isEulerBorrow = isEuler && selectedSolver?.mode === 'borrow'
  const isEulerSpend = isEuler && selectedSolver?.mode === 'spend'
  const stepIndex = STEPS.indexOf(step)
  const activeEulerVaults = selectedCluster === 'prime' ? primeEulerVaults : yieldEulerVaults
  const activeEulerSpendVaults = selectedCluster === 'prime' ? primeEulerSpendVaults : yieldEulerSpendVaults
  const wizardOptions = useMemo(
    () =>
      getAddCardWizardOptions({
        hasHypurrFiBorrow: hypurrFiBorrowTokens.length > 0,
        hasHypurrFiSpend: hypurrFiWithdrawTokens.length > 0,
        hasPrimeEulerBorrow: primeEulerVaults.length > 0,
        hasYieldEulerBorrow: yieldEulerVaults.length > 0,
        hasPrimeEulerSpend: primeEulerSpendVaults.length > 0,
        hasYieldEulerSpend: yieldEulerSpendVaults.length > 0,
      }),
    [
      hypurrFiBorrowTokens.length,
      hypurrFiWithdrawTokens.length,
      primeEulerVaults.length,
      yieldEulerVaults.length,
      primeEulerSpendVaults.length,
      yieldEulerSpendVaults.length,
    ],
  )
  const defaultSolverModeSelection = useMemo<'borrow' | 'spend'>(() => {
    if (wizardOptions.borrowOptions.length > 0) return 'borrow'
    if (wizardOptions.spendOptions.length > 0) return 'spend'
    return 'borrow'
  }, [wizardOptions.borrowOptions.length, wizardOptions.spendOptions.length])
  const visibleSolverStepOptions = useMemo(
    () => (solverModeSelection === 'borrow' ? wizardOptions.borrowOptions : wizardOptions.spendOptions),
    [solverModeSelection, wizardOptions.borrowOptions, wizardOptions.spendOptions],
  )
  const wizardBorrowTokenOptions = useMemo(
    () =>
      selectedSolver
        ? getWizardBorrowTokenOptions({
            market: selectedSolver.market,
            mode: selectedSolver.mode,
            hypurrFiBorrowTokens,
            hypurrFiWithdrawTokens,
          })
        : [],
    [selectedSolver, hypurrFiBorrowTokens, hypurrFiWithdrawTokens],
  )
  const wizardTokenFieldMeta = useMemo(
    () =>
      selectedSolver
        ? getWizardTokenFieldMeta({
            market: selectedSolver.market,
            mode: selectedSolver.mode,
          })
        : null,
    [selectedSolver],
  )

  const autoSelection = useCardAutoSelection(open)
  const highlightedSolverOption = useMemo(() => {
    if (selectedSolver?.mode === solverModeSelection) {
      return selectedSolver
    }
    if (autoSelection.bestOption?.mode === solverModeSelection) {
      return autoSelection.bestOption
    }
    return visibleSolverStepOptions[0] ?? null
  }, [autoSelection.bestOption, selectedSolver, solverModeSelection, visibleSolverStepOptions])
  const { pairs: eulerBorrowPairs, isLoading: isEulerBorrowPairsLoading } = useBorrowPairs(address)

  useEffect(() => {
    const hasOptionsForMode =
      solverModeSelection === 'borrow' ? wizardOptions.borrowOptions.length > 0 : wizardOptions.spendOptions.length > 0

    if (!hasOptionsForMode) {
      setSolverModeSelection(defaultSolverModeSelection)
    }
  }, [
    defaultSolverModeSelection,
    solverModeSelection,
    wizardOptions.borrowOptions.length,
    wizardOptions.spendOptions.length,
  ])

  const activeEulerVaultAddresses = useMemo(() => activeEulerVaults.map((vault) => vault.address), [activeEulerVaults])
  const primeEulerVaultAddresses = useMemo(() => primeEulerVaults.map((vault) => vault.address), [primeEulerVaults])
  const yieldEulerVaultAddresses = useMemo(() => yieldEulerVaults.map((vault) => vault.address), [yieldEulerVaults])

  const activeEulerCollateralVaultAddresses = useMemo(() => {
    const activeBorrowVaults = new Set(activeEulerVaultAddresses.map((address) => address.toLowerCase()))
    return [
      ...new Set(
        eulerBorrowPairs
          .filter((pair) => pair.liability && activeBorrowVaults.has(pair.liability.toLowerCase()) && pair.collateral)
          .map((pair) => pair.collateral!.toLowerCase()),
      ),
    ]
  }, [eulerBorrowPairs, activeEulerVaultAddresses])
  const primeEulerCollateralVaultAddresses = useMemo(() => {
    const primeBorrowVaults = new Set(primeEulerVaultAddresses.map((address) => address.toLowerCase()))
    return [
      ...new Set(
        eulerBorrowPairs
          .filter((pair) => pair.liability && primeBorrowVaults.has(pair.liability.toLowerCase()) && pair.collateral)
          .map((pair) => pair.collateral!.toLowerCase()),
      ),
    ]
  }, [eulerBorrowPairs, primeEulerVaultAddresses])
  const yieldEulerCollateralVaultAddresses = useMemo(() => {
    const yieldBorrowVaults = new Set(yieldEulerVaultAddresses.map((address) => address.toLowerCase()))
    return [
      ...new Set(
        eulerBorrowPairs
          .filter((pair) => pair.liability && yieldBorrowVaults.has(pair.liability.toLowerCase()) && pair.collateral)
          .map((pair) => pair.collateral!.toLowerCase()),
      ),
    ]
  }, [eulerBorrowPairs, yieldEulerVaultAddresses])

  const visibleEulerPositions = useMemo(
    () =>
      getWizardEulerPositions({
        allPositions: autoSelection.allEulerPositions,
        activeEulerVaultAddresses,
        activeEulerCollateralVaultAddresses,
      })
        .filter((position) => !position.hasUnsupportedDebt)
        .sort((a, b) => {
          const aUSD = a.collateral.reduce((sum, collateral) => sum + collateral.depositAmountUSD, 0)
          const bUSD = b.collateral.reduce((sum, collateral) => sum + collateral.depositAmountUSD, 0)
          return bUSD - aUSD
        }),
    [autoSelection.allEulerPositions, activeEulerVaultAddresses, activeEulerCollateralVaultAddresses],
  )
  const primeEulerPreviewPositions = useMemo(
    () =>
      getWizardEulerPositions({
        allPositions: autoSelection.allEulerPositions,
        activeEulerVaultAddresses: primeEulerVaultAddresses,
        activeEulerCollateralVaultAddresses: primeEulerCollateralVaultAddresses,
      }).filter((position) => !position.hasUnsupportedDebt),
    [autoSelection.allEulerPositions, primeEulerVaultAddresses, primeEulerCollateralVaultAddresses],
  )
  const yieldEulerPreviewPositions = useMemo(
    () =>
      getWizardEulerPositions({
        allPositions: autoSelection.allEulerPositions,
        activeEulerVaultAddresses: yieldEulerVaultAddresses,
        activeEulerCollateralVaultAddresses: yieldEulerCollateralVaultAddresses,
      }).filter((position) => !position.hasUnsupportedDebt),
    [autoSelection.allEulerPositions, yieldEulerVaultAddresses, yieldEulerCollateralVaultAddresses],
  )

  const selectedPosition = useMemo(
    () => (isNewPosition ? null : visibleEulerPositions.find((p) => p.subAccountId === subAccountId)),
    [visibleEulerPositions, subAccountId, isNewPosition],
  )
  const nextEulerSubAccountId = useMemo(() => {
    if (autoSelection.allEulerPositions.length === 0) return 0
    return Math.max(...autoSelection.allEulerPositions.map((position) => position.subAccountId)) + 1
  }, [autoSelection.allEulerPositions])

  const fixedDebtVault = useMemo(() => {
    if (!isEulerBorrow || isNewPosition || !selectedPosition?.debtVaults.length) return null

    const debtVaultAddress = selectedPosition.debtVaults[0]?.liabilityVaultAddress.toLowerCase()
    return activeEulerVaults.find((vault) => vault.address.toLowerCase() === debtVaultAddress) ?? null
  }, [isEulerBorrow, isNewPosition, selectedPosition, activeEulerVaults])

  useEffect(() => {
    if (!isEulerBorrow) return
    if (isNewPosition) {
      if (subAccountId !== nextEulerSubAccountId) {
        setSubAccountId(nextEulerSubAccountId)
      }
      return
    }
    if (visibleEulerPositions.some((position) => position.subAccountId === subAccountId)) return

    setIsNewPosition(true)
    setSubAccountId(nextEulerSubAccountId)
  }, [isEulerBorrow, isNewPosition, subAccountId, visibleEulerPositions, nextEulerSubAccountId])

  useEffect(() => {
    if (!fixedDebtVault) return
    if (selectedVault?.address === fixedDebtVault.address) return
    setSelectedVault(fixedDebtVault)
  }, [fixedDebtVault, selectedVault])

  const activeEulerSpendVaultAddresses = useMemo(
    () => activeEulerSpendVaults.map((vault) => vault.address.toLowerCase()),
    [activeEulerSpendVaults],
  )

  const pooledBorrowMarketData = useHypurrFiCardData({
    chainId: hyperEVM.id,
    enabled: open,
  })
  const pooledBorrowApyByToken = useMemo(() => {
    const entries = pooledBorrowMarketData.marketInfo.reserves.map((reserve) => [
      reserve.token.address.toLowerCase(),
      reserve.variableBorrowApy?.multipliedBy(100).toNumber() ?? 0,
    ])
    return Object.fromEntries(entries)
  }, [pooledBorrowMarketData.marketInfo.reserves])
  const eulerBorrowApyByVault = useMemo(() => {
    const apyByVault: Record<string, number> = {}

    for (const pair of eulerBorrowPairs) {
      if (!pair.liability || pair.borrowAPY == null) continue
      const key = pair.liability.toLowerCase()
      apyByVault[key] = Math.max(apyByVault[key] ?? 0, pair.borrowAPY)
    }

    return apyByVault
  }, [eulerBorrowPairs])

  const visibleEulerSpendAssets = useMemo(() => {
    const activeSpendVaultAddresses = new Set(activeEulerSpendVaultAddresses)
    return (autoSelection.eulerSpendData.spendModeCollateralPositions as EulerSpendWizardAsset[])
      .filter((asset) => activeSpendVaultAddresses.has(asset.vaultAddress.toLowerCase()))
      .sort((a, b) => b.spendingPowerUSD.minus(a.spendingPowerUSD).toNumber())
  }, [autoSelection.eulerSpendData.spendModeCollateralPositions, activeEulerSpendVaultAddresses])

  const supportedSpendCollateralLabels = useMemo(
    () => [...new Set(activeEulerSpendVaults.map((vault) => vault.label))],
    [activeEulerSpendVaults],
  )

  const spendCollateralIconDisplay = useMemo(
    () => getSpendCollateralSupportedIconDisplay(supportedSpendCollateralLabels),
    [supportedSpendCollateralLabels],
  )

  const resetWizard = useCallback(() => {
    setStep('solver')
    setSolverModeSelection(defaultSolverModeSelection)
    setSelectedSolver(null)
    setSelectedCluster(null)
    setSelectedVault(null)
    setSelectedBorrowToken(null)
    setSelectedSpendAsset(null)
    setSubAccountId(0)
    setIsNewPosition(false)
    setCardLabel('')
    setCreatedCardId(null)
    setIsSuccess(false)
    setLabelError(null)
    setConfigError(null)
    setSolverPreviewSelections({})
    resetCreate()
  }, [defaultSolverModeSelection, resetCreate])

  const handleClose = (nextOpen: boolean) => {
    if (!nextOpen) resetWizard()
    onOpenChange(nextOpen)
  }

  const handleSelectSolver = (solver: AddCardWizardOption) => {
    const cluster = solver.cluster ?? null
    const isSpend = solver.mode === 'spend'
    const shouldUseSuggestedConfig = autoSelection.bestOption?.key === solver.key
    const suggestedConfig = shouldUseSuggestedConfig ? autoSelection.bestCardSolverConfig : null
    const previewSuggestedSubAccountId = solverPreviewSelections[solver.key]
    const clusterBorrowVaults = cluster === 'prime' ? primeEulerVaults : yieldEulerVaults
    const clusterSpendVaults = cluster === 'prime' ? primeEulerSpendVaults : yieldEulerSpendVaults
    const clusterVaults = isSpend ? clusterSpendVaults : clusterBorrowVaults
    const suggestedVaultAddress =
      typeof suggestedConfig?.borrowVaultAddress === 'string' ? suggestedConfig.borrowVaultAddress : undefined
    const suggestedSubAccountId =
      typeof suggestedConfig?.subAccountId === 'number'
        ? suggestedConfig.subAccountId
        : typeof previewSuggestedSubAccountId === 'number'
          ? previewSuggestedSubAccountId
          : 0
    const suggestedBorrowTokenAddress =
      typeof suggestedConfig?.borrowTokenAddress === 'string' ? suggestedConfig.borrowTokenAddress : undefined
    const availableBorrowTokens = solver.mode === 'spend' ? hypurrFiWithdrawTokens : hypurrFiBorrowTokens
    setSolverModeSelection(solver.mode)
    setSelectedSolver(solver)
    setSelectedCluster(cluster)
    setSelectedVault(
      getInitialWizardEulerVault({
        market: solver.market,
        availableEulerVaults: clusterVaults,
        previousSelectedVaultAddress: suggestedVaultAddress ?? selectedVault?.address,
      }),
    )
    setSelectedBorrowToken(
      suggestedBorrowTokenAddress
        ? availableBorrowTokens.find(
            (token) => token.address.toLowerCase() === suggestedBorrowTokenAddress.toLowerCase(),
          ) ?? null
        : null,
    )
    setSelectedSpendAsset(null)
    setSubAccountId(suggestedSubAccountId)
    setIsNewPosition(false)
  }

  const handleProceedFromSolver = () => {
    const solverToUse =
      (selectedSolver?.mode === solverModeSelection ? selectedSolver : null) ??
      highlightedSolverOption ??
      visibleSolverStepOptions[0] ??
      null

    if (!solverToUse) return

    handleSelectSolver(solverToUse)
    setStep('config')
  }
  const handleSolverPreviewSelection = useCallback((solverKey: string, subAccountId: number) => {
    setSolverPreviewSelections((current) => {
      if (current[solverKey] === subAccountId) {
        return current
      }

      return {
        ...current,
        [solverKey]: subAccountId,
      }
    })
  }, [])

  const canProceedFromConfig = useMemo(() => {
    if (!selectedSolver) return false
    if (isEulerBorrow && !selectedVault) return false
    if (!isEuler && !selectedBorrowToken) return false
    if (isEulerSpend && !selectedSpendAsset) return false
    if (isEulerBorrow && !isNewPosition && selectedPosition?.hasUnsupportedDebt) return false
    return true
  }, [
    selectedSolver,
    isEuler,
    isEulerBorrow,
    isEulerSpend,
    selectedVault,
    selectedBorrowToken,
    selectedSpendAsset,
    isNewPosition,
    selectedPosition,
  ])
  const requiredSelectionError = useMemo(
    () =>
      selectedSolver
        ? getRequiredConfiguredSelectionError({
            mode: selectedSolver.mode,
            market: selectedSolver.market,
            hasSelectedAsset: !!selectedSpendAsset,
            hasBorrowToken: !!selectedBorrowToken,
            hasEulerVault: !!selectedVault,
            isBorrowTokenFixedByPosition: !!fixedDebtVault,
          })
        : null,
    [fixedDebtVault, selectedBorrowToken, selectedSolver, selectedSpendAsset, selectedVault],
  )
  const handleCreate = async () => {
    setConfigError(null)
    if (!address || !selectedSolver) return
    if (isEulerBorrow && !selectedVault) return
    if (!isEuler && !selectedBorrowToken) return
    if (isEulerSpend && !selectedSpendAsset) return

    let solverConfig: Record<string, unknown>
    if (isEulerBorrow) {
      solverConfig = {
        borrowVaultAddress: fixedDebtVault?.address ?? selectedVault!.address,
        subAccountId: isNewPosition ? nextEulerSubAccountId : subAccountId,
      }
    } else if (isEulerSpend) {
      solverConfig = {
        borrowVaultAddress: selectedSpendAsset!.vaultAddress,
        supplyVaultAddress: selectedSpendAsset!.vaultAddress,
        subAccountId: selectedSpendAsset!.subAccountId,
      }
    } else if (selectedSolver.mode === 'spend') {
      const resolvedAToken = hypurrFiATokenByBaseToken[selectedBorrowToken!.address.toLowerCase()]
      if (!resolvedAToken) {
        setConfigError('Could not resolve aToken for the selected token. Please try a different token.')
        return
      }
      solverConfig = { aTokenAddress: resolvedAToken }
    } else {
      solverConfig = { borrowTokenAddress: selectedBorrowToken!.address }
    }

    try {
      const result = await createCard({
        solverType: selectedSolver.solverType,
        chainID: hyperEVM.id,
        cardSolverConfig: solverConfig,
      })

      const newCardId = result?.data?.cardID
      setCreatedCardId(newCardId ?? null)
      if (newCardId && cardLabel.trim()) {
        try {
          await updateLabel({ cardID: newCardId, label: cardLabel.trim() })
        } catch {
          // Card created but label failed — non-critical, user can set from detail view
        }
      }

      setIsSuccess(true)
    } catch {
      // handled by mutation error state
    }
  }

  const solverDisplayName = selectedSolver?.title ?? ''
  const solverMode = selectedSolver ? getWizardSolverModeLabel(selectedSolver.mode) : 'Borrow'
  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[900px]">
        <MultiPanelDialog className="max-w-none">
          <DialogTitle>{isSuccess ? 'Card Created' : 'Add New Card'}</DialogTitle>

          {!isSuccess && !isMaxed && (
            <div className="mb-4 flex items-center justify-center gap-2">
              {STEPS.map((s, i) => (
                <div key={s} className="flex items-center gap-2">
                  <div
                    className={cn(
                      'flex h-6 w-6 items-center justify-center rounded-full font-medium text-xs transition-colors',
                      i < stepIndex
                        ? 'bg-primary text-primary-foreground'
                        : i === stepIndex
                          ? 'border-2 border-primary text-primary'
                          : 'border border-muted-foreground/30 text-muted-foreground/50',
                    )}
                  >
                    {i < stepIndex ? <Check className="h-3 w-3" /> : i + 1}
                  </div>
                  {i < STEPS.length - 1 && (
                    <div className={cn('h-px w-6', i < stepIndex ? 'bg-primary' : 'bg-muted-foreground/20')} />
                  )}
                </div>
              ))}
            </div>
          )}

          {isMaxed && !isSuccess && (
            <DialogPanel className="items-center gap-4 py-8">
              <AlertTriangle className="h-10 w-10 text-amber-400" />
              <Typography variant="p" className="text-center text-muted-foreground">
                Maximum of {effectiveMaxCards} cards allowed.
              </Typography>
              <Button variant="outline" onClick={() => handleClose(false)}>
                Close
              </Button>
            </DialogPanel>
          )}

          {isSuccess && (
            <DialogPanel className="items-center gap-4 py-8">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-500/20">
                <Check className="h-6 w-6 text-green-400" />
              </div>
              <Typography variant="h4" className="text-center font-semibold">
                Card created successfully
              </Typography>
              <Typography variant="p" className="text-center text-muted-foreground text-sm">
                {solverDisplayName} &middot; {solverMode}
                {cardLabel.trim() ? ` · "${cardLabel.trim()}"` : ''}
              </Typography>
              {isNewPosition && (
                <Typography variant="p" className="text-center text-blue-300 text-xs">
                  Continue in Card Settings to fund this new position before you start spending.
                </Typography>
              )}
              <div className="flex gap-3">
                {isNewPosition && (
                  <Button
                    onClick={() => {
                      onCardCreated?.({ needsFunding: true, cardId: createdCardId ?? undefined })
                      handleClose(false)
                    }}
                    className="bg-gradient-to-r from-gradient-yellow to-gradient-green text-black"
                  >
                    Set Up Position
                  </Button>
                )}
                <Button
                  variant={isNewPosition ? 'outline' : 'primary'}
                  onClick={() => {
                    onCardCreated?.({ needsFunding: false, cardId: createdCardId ?? undefined })
                    handleClose(false)
                  }}
                >
                  Done
                </Button>
              </div>
            </DialogPanel>
          )}

          {/* Step 1: Select Solver */}
          {!isMaxed && !isSuccess && step === 'solver' && (
            <DialogPanel className="gap-6">
              {isSolverOptionsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-6 w-6 animate-spin text-primary" />
                </div>
              ) : (
                <WizardStepLayout
                  main={
                    <Panel.Wrapper className="relative overflow-hidden">
                      <div className="pointer-events-none absolute inset-0 rounded-lg bg-gradient-to-br from-gradient-yellow/5 via-transparent to-gradient-green/5" />
                      <Panel.Content className="relative space-y-6 p-6">
                        <div className="space-y-2">
                          <Typography variant="p" className="font-medium text-base">
                            Choose how your card settles on-chain
                          </Typography>
                          <Typography variant="p" className="text-muted-foreground text-sm leading-relaxed">
                            Pick a mode first, then a market. You&apos;ll configure the exact position, vault, or
                            collateral route in the next step.
                          </Typography>
                        </div>

                        <div className="space-y-5">
                          <div>
                            <Typography variant="p" className="mb-2 font-medium text-muted-foreground text-sm">
                              Mode
                            </Typography>
                            <WizardModeToggle
                              mode={solverModeSelection}
                              onChange={setSolverModeSelection}
                              canBorrow={wizardOptions.borrowOptions.length > 0}
                              canSpend={wizardOptions.spendOptions.length > 0}
                            />
                          </div>

                          <div>
                            <Typography variant="p" className="mb-2 font-medium text-muted-foreground text-sm">
                              Market
                            </Typography>
                            {visibleSolverStepOptions.length > 0 ? (
                              <div className="grid gap-3 md:grid-cols-3">
                                {visibleSolverStepOptions.map((solver) =>
                                  solver.market === 'euler' && solver.mode === 'borrow' ? (
                                    <EulerBorrowSolverOptionButton
                                      key={solver.key}
                                      solverKey={solver.key}
                                      title={solver.title}
                                      subtitle={solver.subtitle}
                                      positions={
                                        solver.cluster === 'prime'
                                          ? primeEulerPreviewPositions
                                          : yieldEulerPreviewPositions
                                      }
                                      fallbackBorrowVaultAddress={
                                        (solver.cluster === 'prime'
                                          ? primeEulerVaults[0]?.address
                                          : yieldEulerVaults[0]?.address) as Address | undefined
                                      }
                                      userAddress={address}
                                      chainId={hyperEVM.id}
                                      defaultValueLabel={formatSolverOptionValue(
                                        autoSelection.optionValues[solver.key],
                                        autoSelection.isLoading,
                                      )}
                                      defaultDetail={autoSelection.optionDetails[solver.key]}
                                      onBestPositionResolved={handleSolverPreviewSelection}
                                      selected={
                                        selectedSolver?.key === solver.key ||
                                        highlightedSolverOption?.key === solver.key
                                      }
                                      onClick={() => handleSelectSolver(solver)}
                                    />
                                  ) : (
                                    <SolverOptionButton
                                      key={solver.key}
                                      title={solver.title}
                                      subtitle={solver.subtitle}
                                      valueLabel={formatSolverOptionValue(
                                        autoSelection.optionValues[solver.key],
                                        autoSelection.isLoading,
                                      )}
                                      detail={autoSelection.optionDetails[solver.key]}
                                      selected={
                                        selectedSolver?.key === solver.key ||
                                        highlightedSolverOption?.key === solver.key
                                      }
                                      onClick={() => handleSelectSolver(solver)}
                                    />
                                  ),
                                )}
                              </div>
                            ) : (
                              <div className="rounded-lg border border-border/60 bg-muted/20 px-4 py-3">
                                <Typography variant="p" className="text-muted-foreground text-sm">
                                  No {solverModeSelection} routes are currently available for this wallet.
                                </Typography>
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="mt-2 flex justify-end">
                          <Button
                            size="sm"
                            onClick={handleProceedFromSolver}
                            disabled={visibleSolverStepOptions.length === 0}
                            postfixIcon={<ChevronRight className="h-4 w-4" />}
                          >
                            Next
                          </Button>
                        </div>
                      </Panel.Content>
                    </Panel.Wrapper>
                  }
                />
              )}
            </DialogPanel>
          )}

          {/* Step 2: Configure Solver */}
          {!isMaxed && !isSuccess && step === 'config' && selectedSolver && (
            <DialogPanel className="gap-6">
              <WizardStepLayout
                main={
                  <Panel.Wrapper className="relative overflow-hidden">
                    <div className="pointer-events-none absolute inset-0 rounded-lg bg-gradient-to-br from-gradient-yellow/5 via-transparent to-gradient-green/5" />
                    <Panel.Content className="relative space-y-5 p-6">
                      <WizardStepBack
                        onClick={() => {
                          setStep('solver')
                          setSelectedSolver(null)
                          setSelectedCluster(null)
                          setSelectedVault(null)
                        }}
                      />
                      <div className="space-y-3">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="rounded-full border border-border/70 bg-muted/30 px-2.5 py-1 font-medium text-[11px] text-muted-foreground uppercase tracking-wide">
                            {solverMode}
                          </span>
                          <span className="rounded-full border border-primary/20 bg-primary/10 px-2.5 py-1 font-medium text-[11px] text-primary uppercase tracking-wide">
                            {solverDisplayName}
                          </span>
                        </div>
                        <Typography variant="p" className="font-medium text-base">
                          Configure {solverDisplayName}
                        </Typography>
                        <Typography variant="p" className="text-muted-foreground text-sm leading-relaxed">
                          Choose the exact position, borrow token, vault, or collateral source for this card.
                        </Typography>
                      </div>

                      {isEulerBorrow ? (
                        <>
                          <div>
                            <Typography variant="p" className="mb-2 font-medium text-sm">
                              Borrow Token
                            </Typography>
                            <div className="flex flex-col gap-2">
                              {fixedDebtVault ? (
                                <div className="flex w-full items-center justify-between rounded-lg border border-border bg-input px-3 py-3 text-sm">
                                  <div className="flex items-center gap-2">
                                    <Image
                                      src={getTokenImage(TokenSymbol(fixedDebtVault.borrowTokenSymbol))}
                                      alt={fixedDebtVault.borrowTokenSymbol}
                                      width={24}
                                      height={24}
                                      className="rounded-full"
                                    />
                                    <span className="font-medium">{fixedDebtVault.borrowTokenSymbol}</span>
                                    <span className="text-muted-foreground text-xs">{fixedDebtVault.label}</span>
                                    <span className="text-muted-foreground text-xs">(Fixed by Position)</span>
                                  </div>
                                  <div className="text-right">
                                    <Typography variant="p" className="text-[11px] text-muted-foreground">
                                      Borrow APY
                                    </Typography>
                                    <Typography variant="p" className="font-medium text-xs">
                                      {formatApy(eulerBorrowApyByVault[fixedDebtVault.address.toLowerCase()] ?? 0)}
                                    </Typography>
                                  </div>
                                </div>
                              ) : (
                                activeEulerVaults.map((vault) => (
                                  <button
                                    key={vault.address}
                                    onClick={() => setSelectedVault(vault)}
                                    className={cn(
                                      'flex items-center justify-between rounded-lg border p-3 text-left text-sm transition-colors',
                                      selectedVault?.address === vault.address
                                        ? 'border-primary bg-primary/10'
                                        : 'border-border hover:border-primary',
                                    )}
                                  >
                                    <div className="flex items-center gap-2">
                                      <Image
                                        src={getTokenImage(TokenSymbol(vault.borrowTokenSymbol))}
                                        alt={vault.borrowTokenSymbol}
                                        width={28}
                                        height={28}
                                        className="rounded-full"
                                      />
                                      <div>
                                        <span className="font-medium">{vault.borrowTokenSymbol}</span>
                                        <span className="ml-2 text-muted-foreground text-xs">{vault.label}</span>
                                      </div>
                                    </div>
                                    <div className="text-right">
                                      <Typography variant="p" className="text-[11px] text-muted-foreground">
                                        Borrow APY
                                      </Typography>
                                      <Typography variant="p" className="font-medium text-xs">
                                        {formatApy(eulerBorrowApyByVault[vault.address.toLowerCase()] ?? 0)}
                                      </Typography>
                                    </div>
                                  </button>
                                ))
                              )}
                            </div>
                          </div>

                          <div data-testid="position-select" data-value={isNewPosition ? 'new' : String(subAccountId)}>
                            <Typography variant="p" className="mb-2 font-medium text-sm">
                              Select Position
                            </Typography>
                            {autoSelection.isLoadingEulerPositions || isEulerBorrowPairsLoading ? (
                              <div className="flex flex-col gap-3">
                                {[0, 1].map((i) => (
                                  <div
                                    key={i}
                                    className="animate-pulse rounded-xl border border-border/50 bg-secondary/20 p-4"
                                  >
                                    <div className="flex items-center justify-between">
                                      <div className="flex items-center gap-2">
                                        <div className="h-6 w-6 rounded-full bg-muted" />
                                        <div className="flex flex-col gap-1">
                                          <div className="h-4 w-20 rounded bg-muted" />
                                          <div className="h-3 w-16 rounded bg-muted" />
                                        </div>
                                      </div>
                                      <div className="flex flex-col items-end gap-1">
                                        <div className="h-3 w-20 rounded bg-muted" />
                                        <div className="h-4 w-14 rounded bg-muted" />
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className="flex flex-col gap-3">
                                {visibleEulerPositions.map((pos) => (
                                  <WizardEulerPositionCard
                                    key={pos.subAccountId}
                                    position={pos}
                                    isSelected={!isNewPosition && subAccountId === pos.subAccountId}
                                    fallbackBorrowVaultAddress={selectedVault?.address as Address | undefined}
                                    userAddress={address}
                                    chainId={hyperEVM.id}
                                    onClick={() => {
                                      setIsNewPosition(false)
                                      setSubAccountId(pos.subAccountId)
                                    }}
                                  />
                                ))}
                                <button
                                  type="button"
                                  onClick={() => {
                                    setIsNewPosition(true)
                                    setSubAccountId(nextEulerSubAccountId)
                                  }}
                                  className={cn(
                                    'group flex w-full items-center justify-center gap-3 rounded-xl border border-dashed p-4 transition-all duration-200',
                                    isNewPosition
                                      ? 'border-primary/50 bg-primary/5 text-primary shadow-sm ring-1 ring-primary/20'
                                      : 'border-border/60 bg-transparent text-muted-foreground hover:border-primary/40 hover:bg-secondary/20 hover:text-foreground',
                                  )}
                                >
                                  <div
                                    className={cn(
                                      'flex h-8 w-8 items-center justify-center rounded-full transition-colors',
                                      isNewPosition
                                        ? 'bg-primary/20'
                                        : 'bg-secondary group-hover:bg-primary/10 group-hover:text-primary',
                                    )}
                                  >
                                    <Plus className="h-4 w-4" />
                                  </div>
                                  <span className="font-medium text-sm">
                                    Create New Position (#{nextEulerSubAccountId})
                                  </span>
                                </button>
                              </div>
                            )}
                            {isNewPosition && (
                              <div className="mt-2 rounded-lg border border-blue-500/20 bg-blue-500/5 px-3 py-2">
                                <Typography variant="p" className="text-blue-300 text-xs">
                                  A new position will be created. You&apos;ll need to fund it after card creation.
                                </Typography>
                              </div>
                            )}
                          </div>
                        </>
                      ) : isEulerSpend ? (
                        <div className="flex flex-col gap-3">
                          <div>
                            <Typography variant="p" className="mb-2 font-medium text-sm">
                              Select Collateral to Spend
                            </Typography>
                            {visibleEulerSpendAssets.length > 0 ? (
                              <div className="flex flex-col gap-2">
                                {visibleEulerSpendAssets.map((asset) => (
                                  <button
                                    key={`${asset.vaultAddress}-${asset.subAccountId}`}
                                    onClick={() => setSelectedSpendAsset(asset)}
                                    className={cn(
                                      'flex items-center justify-between rounded-lg border p-3 text-left text-sm transition-colors',
                                      selectedSpendAsset?.vaultAddress === asset.vaultAddress &&
                                        selectedSpendAsset?.subAccountId === asset.subAccountId
                                        ? 'border-primary bg-primary/10'
                                        : 'border-border hover:border-primary',
                                    )}
                                  >
                                    <div className="flex items-center gap-2">
                                      <Image
                                        src={getTokenImage(TokenSymbol(asset.token.symbol))}
                                        alt={asset.token.symbol}
                                        width={28}
                                        height={28}
                                        className="rounded-full"
                                      />
                                      <div>
                                        <span className="font-medium">{asset.token.symbol}</span>
                                        <span className="ml-2 text-muted-foreground text-xs">
                                          Position {asset.subAccountId}
                                        </span>
                                      </div>
                                    </div>
                                    <Typography variant="p" className="font-medium text-xs">
                                      {formatUsd(asset.spendingPowerUSD.toNumber())}
                                    </Typography>
                                  </button>
                                ))}
                              </div>
                            ) : isSolverOptionsLoading ? (
                              <div className="rounded-lg border border-border/50 bg-muted/20 p-4">
                                <div className="flex items-center gap-3 text-muted-foreground text-sm">
                                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary/20 border-t-primary" />
                                  <span>Loading supported collateral options...</span>
                                </div>
                              </div>
                            ) : (
                              <div className="rounded-lg border border-yellow-500/20 bg-yellow-500/10 p-4">
                                {spendCollateralIconDisplay.visibleLabels.length > 0 && (
                                  <div className="mb-3 flex items-center">
                                    {spendCollateralIconDisplay.visibleLabels.map((label, index) => (
                                      <div
                                        key={label}
                                        className="relative h-5 w-5 rounded-full border-2 border-background"
                                        style={{
                                          marginLeft: index > 0 ? '-6px' : '0',
                                          zIndex: spendCollateralIconDisplay.visibleLabels.length - index,
                                        }}
                                      >
                                        <Image
                                          src={getTokenImage(TokenSymbol(label))}
                                          alt={label}
                                          width={20}
                                          height={20}
                                          className="rounded-full"
                                        />
                                      </div>
                                    ))}
                                    {spendCollateralIconDisplay.remainingCount > 0 && (
                                      <div
                                        className="relative flex h-5 w-5 items-center justify-center rounded-full border-2 border-background bg-muted font-semibold text-[10px]"
                                        style={{ marginLeft: '-6px', zIndex: 0 }}
                                      >
                                        +{spendCollateralIconDisplay.remainingCount}
                                      </div>
                                    )}
                                  </div>
                                )}
                                <Typography variant="p" className="text-sm text-yellow-400">
                                  {getSpendCollateralEmptyStateMessage(supportedSpendCollateralLabels)}
                                </Typography>
                              </div>
                            )}
                          </div>
                          <div
                            className={cn(
                              'rounded-lg border px-3 py-2.5',
                              wizardTokenFieldMeta?.toneClassName ?? 'border-border bg-background',
                            )}
                          >
                            <Typography
                              variant="p"
                              className={cn(
                                'text-xs leading-relaxed',
                                wizardTokenFieldMeta?.textClassName ?? 'text-muted-foreground',
                              )}
                            >
                              {wizardTokenFieldMeta?.description}
                            </Typography>
                          </div>
                        </div>
                      ) : (
                        <div className="flex flex-col gap-3">
                          <div>
                            <Typography variant="p" className="mb-2 font-medium text-sm">
                              {wizardTokenFieldMeta?.label}
                            </Typography>
                            <div className="flex flex-col gap-2">
                              {wizardBorrowTokenOptions.map((token) => (
                                <button
                                  key={token.address}
                                  onClick={() => setSelectedBorrowToken(token)}
                                  className={cn(
                                    'flex items-center justify-between rounded-lg border p-3 text-left text-sm transition-colors',
                                    selectedBorrowToken?.address === token.address
                                      ? 'border-primary bg-primary/10'
                                      : 'border-border hover:border-primary',
                                  )}
                                >
                                  <div className="flex items-center gap-2">
                                    <Image
                                      src={getTokenImage(TokenSymbol(token.symbol))}
                                      alt={token.symbol}
                                      width={28}
                                      height={28}
                                      className="rounded-full"
                                    />
                                    <span className="font-medium">{token.symbol}</span>
                                  </div>
                                  <div className="text-right">
                                    <Typography variant="p" className="text-[11px] text-muted-foreground">
                                      Borrow APY
                                    </Typography>
                                    <Typography variant="p" className="font-medium text-xs">
                                      {formatApy(pooledBorrowApyByToken[token.address.toLowerCase()] ?? 0)}
                                    </Typography>
                                  </div>
                                </button>
                              ))}
                            </div>
                          </div>
                          <div
                            className={cn(
                              'rounded-lg border px-3 py-2.5',
                              wizardTokenFieldMeta?.toneClassName ?? 'border-border bg-background',
                            )}
                          >
                            <Typography
                              variant="p"
                              className={cn(
                                'text-xs leading-relaxed',
                                wizardTokenFieldMeta?.textClassName ?? 'text-muted-foreground',
                              )}
                            >
                              {wizardTokenFieldMeta?.description}
                            </Typography>
                          </div>
                        </div>
                      )}

                      {requiredSelectionError && !canProceedFromConfig && (
                        <div className="rounded-lg border border-amber-500/20 bg-amber-500/10 px-3 py-2.5">
                          <Typography variant="p" className="text-amber-300 text-sm">
                            {requiredSelectionError}
                          </Typography>
                        </div>
                      )}

                      <div className="mt-2 flex justify-end">
                        <Button
                          size="sm"
                          onClick={() => setStep('name')}
                          disabled={!canProceedFromConfig}
                          postfixIcon={<ChevronRight className="h-4 w-4" />}
                        >
                          Next
                        </Button>
                      </div>
                    </Panel.Content>
                  </Panel.Wrapper>
                }
              />
            </DialogPanel>
          )}

          {/* Step 3: Name Your Card */}
          {!isMaxed && !isSuccess && step === 'name' && (
            <DialogPanel className="gap-6">
              <WizardStepLayout
                main={
                  <Panel.Wrapper className="relative overflow-hidden">
                    <div className="pointer-events-none absolute inset-0 rounded-lg bg-gradient-to-br from-gradient-yellow/5 via-transparent to-gradient-green/5" />
                    <Panel.Content className="relative space-y-5 p-6">
                      <WizardStepBack onClick={() => setStep('config')} />
                      <div className="space-y-2">
                        <Typography variant="p" className="font-medium text-base">
                          Give this card a name
                        </Typography>
                        <Typography variant="p" className="text-muted-foreground text-sm">
                          Labels are optional, but they make the new card easier to distinguish inside the dashboard.
                        </Typography>
                      </div>

                      <div>
                        <input
                          type="text"
                          value={cardLabel}
                          onChange={(e) => {
                            const val = e.target.value
                            if (val.length <= LABEL_MAX_LENGTH) {
                              setCardLabel(val)
                              setLabelError(null)
                            } else {
                              setLabelError(`Label must be ${LABEL_MAX_LENGTH} characters or less`)
                            }
                          }}
                          placeholder='e.g. "Daily Spending", "Euler USDC Borrow"'
                          className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-sm focus:border-primary placeholder:text-muted-foreground/50 focus:outline-none"
                          maxLength={LABEL_MAX_LENGTH}
                        />
                        <div className="mt-1.5 flex items-center justify-between">
                          {labelError ? (
                            <Typography variant="p" className="text-red-400 text-xs">
                              {labelError}
                            </Typography>
                          ) : (
                            <span />
                          )}
                          <Typography
                            variant="p"
                            className={cn(
                              'text-xs',
                              cardLabel.length >= LABEL_MAX_LENGTH ? 'text-red-400' : 'text-muted-foreground',
                            )}
                          >
                            {cardLabel.length}/{LABEL_MAX_LENGTH}
                          </Typography>
                        </div>
                      </div>

                      <div className="mt-2 flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setCardLabel('')
                            setStep('review')
                          }}
                        >
                          Skip
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => setStep('review')}
                          postfixIcon={<ChevronRight className="h-4 w-4" />}
                        >
                          Next
                        </Button>
                      </div>
                    </Panel.Content>
                  </Panel.Wrapper>
                }
              />
            </DialogPanel>
          )}

          {/* Step 4: Review & Confirm */}
          {!isMaxed && !isSuccess && step === 'review' && selectedSolver && (
            <DialogPanel className="gap-6">
              <WizardStepLayout
                main={
                  <Panel.Wrapper className="relative overflow-hidden">
                    <div className="pointer-events-none absolute inset-0 rounded-lg bg-gradient-to-br from-gradient-yellow/5 via-transparent to-gradient-green/5" />
                    <Panel.Content className="relative space-y-5 p-6">
                      <WizardStepBack onClick={() => setStep('name')} />
                      <div className="space-y-2">
                        <Typography variant="p" className="font-medium text-base">
                          Review your new card
                        </Typography>
                        <Typography variant="p" className="text-muted-foreground text-sm">
                          Confirm the route, requirements, and label before you create the card.
                        </Typography>
                      </div>

                      <div className="flex flex-col gap-3 rounded-lg border border-border p-4">
                        <ReviewRow label="Solver" value={`${solverDisplayName} · ${solverMode}`} />
                        {isEulerBorrow && selectedVault && (
                          <ReviewRow
                            label="Vault"
                            value={`${selectedVault.label} (${selectedVault.borrowTokenSymbol})`}
                          />
                        )}
                        {isEulerBorrow && (
                          <ReviewRow
                            label="Position"
                            value={isNewPosition ? 'New Position' : `Position ${subAccountId}`}
                          />
                        )}
                        {isEulerBorrow && selectedPosition && selectedPosition.debtVaults.length > 0 && (
                          <ReviewRow
                            label="Debt Vaults"
                            value={selectedPosition.debtVaults.map((d) => d.stableSymbol).join(', ')}
                          />
                        )}
                        {isEulerBorrow && selectedPosition && selectedPosition.collateralOnly && (
                          <ReviewRow
                            label="Collateral"
                            value={selectedPosition.collateral.map((c) => c.tokenSymbol).join(', ')}
                          />
                        )}
                        {isEulerSpend && selectedSpendAsset && (
                          <>
                            <ReviewRow label="Collateral" value={selectedSpendAsset.token.symbol} />
                            <ReviewRow label="Position" value={`Position ${selectedSpendAsset.subAccountId}`} />
                          </>
                        )}
                        {!isEuler && <ReviewRow label="Token" value={selectedBorrowToken?.symbol ?? ''} />}
                        <ReviewRow label="Label" value={cardLabel.trim() || 'No label'} muted={!cardLabel.trim()} />
                      </div>

                      {(createError || configError) && (
                        <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-3">
                          <div className="flex items-start gap-2">
                            <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-400" />
                            <Typography variant="p" className="text-red-300 text-sm">
                              {createError?.message ?? configError}
                            </Typography>
                          </div>
                        </div>
                      )}

                      <div className="mt-2 flex justify-end">
                        <Button size="sm" onClick={handleCreate} disabled={isCreating || isLabeling}>
                          {isCreating || isLabeling ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              {isLabeling ? 'Setting label...' : 'Creating...'}
                            </>
                          ) : (
                            'Create Card'
                          )}
                        </Button>
                      </div>
                    </Panel.Content>
                  </Panel.Wrapper>
                }
              />
            </DialogPanel>
          )}
        </MultiPanelDialog>
      </DialogContent>
    </Dialog>
  )
}

function WizardStepBack({ onClick }: { onClick: () => void }) {
  return (
    <div className="-mb-1">
      <Button variant="ghost" size="sm" onClick={onClick} prefixIcon={<ChevronLeft className="h-4 w-4" />}>
        Back
      </Button>
    </div>
  )
}

function WizardStepLayout({ main }: { main: ReactNode }) {
  return (
    <div className="w-full min-w-0">
      <div className="w-full min-w-0">{main}</div>
    </div>
  )
}

function useWizardEulerPositionPreview({
  position,
  fallbackBorrowVaultAddress,
  userAddress,
  chainId,
}: {
  position: MergedPosition
  fallbackBorrowVaultAddress: Address | undefined
  userAddress: Address | undefined
  chainId: number
}) {
  const { resolveMarket } = useVaultClassifier()
  const liabilityVaultAddress = useMemo(() => {
    if (position.debtVaults.length > 0) {
      return position.debtVaults[0]!.liabilityVaultAddress as Address
    }
    return fallbackBorrowVaultAddress
  }, [position.debtVaults, fallbackBorrowVaultAddress])

  const subAccountAddress = useMemo(() => {
    if (!userAddress) return undefined
    return getSubAccountAddress(userAddress, position.subAccountId)
  }, [userAddress, position.subAccountId])

  const { data: liquidityData, isLoading: isLiquidityLoading } = useAccountLiquidity({
    accountAddress: subAccountAddress,
    liabilityVault: liabilityVaultAddress,
    chainId,
    enabled: !!subAccountAddress && !!liabilityVaultAddress,
  })

  const { enabledVaults, isLoading: isLoadingEnabled } = useEnabledCollateralVaults({
    accountAddress: subAccountAddress,
    chainId,
    enabled: !!subAccountAddress,
  })

  const controllerMarketKey = useMemo(() => {
    if (position.debtVaults.length > 0) {
      return resolveMarket(position.debtVaults[0]!.liabilityVaultAddress as Address)?.key
    }
    if (fallbackBorrowVaultAddress) {
      return resolveMarket(fallbackBorrowVaultAddress)?.key
    }
    return undefined
  }, [position.debtVaults, fallbackBorrowVaultAddress, resolveMarket])

  const { enabledCollateralUSD, savingsUSD, eligibleCollateralItems } = useMemo(() => {
    const enabledSet = new Set(enabledVaults)
    let enabled = 0
    let savings = 0
    const items: { vaultAddress: string; depositAmountUSD: number }[] = []

    for (const collateral of position.collateral) {
      const isEnabled = enabledSet.has(collateral.vaultAddress.toLowerCase())

      if (!isEnabled) {
        savings += collateral.depositAmountUSD
        continue
      }

      if (!controllerMarketKey) {
        enabled += collateral.depositAmountUSD
        items.push({ vaultAddress: collateral.vaultAddress, depositAmountUSD: collateral.depositAmountUSD })
        continue
      }

      const vaultMarketKey = resolveMarket(collateral.vaultAddress as Address)?.key
      if (vaultMarketKey === controllerMarketKey) {
        enabled += collateral.depositAmountUSD
        items.push({ vaultAddress: collateral.vaultAddress, depositAmountUSD: collateral.depositAmountUSD })
      } else {
        savings += collateral.depositAmountUSD
      }
    }

    return { enabledCollateralUSD: enabled, savingsUSD: savings, eligibleCollateralItems: items }
  }, [enabledVaults, position.collateral, controllerMarketKey, resolveMarket])

  const ltvFallback = useLtvWeightedCollateral({
    borrowVaultAddress: liabilityVaultAddress,
    collateral: eligibleCollateralItems,
    chainId,
  })

  const hasAnyDeposit = position.collateral.length > 0
  const hasEnabledCollateral = enabledCollateralUSD > 0
  const hasSavings = savingsUSD > 0
  const savingsOnly = hasAnyDeposit && !hasEnabledCollateral && hasSavings

  const spendingPower = useMemo(() => {
    if (liquidityData != null) {
      return Math.max(liquidityData.maxBorrowCapacityUSD - (liquidityData.totalDebtBorrowUSD ?? 0), 0)
    }
    if (isLiquidityLoading || isLoadingEnabled) return undefined
    if (eligibleCollateralItems.length === 0) return 0
    if (!liabilityVaultAddress) return enabledCollateralUSD
    if (ltvFallback === undefined) return undefined
    return ltvFallback
  }, [
    liquidityData,
    isLiquidityLoading,
    isLoadingEnabled,
    eligibleCollateralItems,
    liabilityVaultAddress,
    enabledCollateralUSD,
    ltvFallback,
  ])

  return {
    liabilityVaultAddress,
    spendingPower,
    savingsUSD,
    hasEnabledCollateral,
    hasSavings,
    savingsOnly,
    isLoading: isLiquidityLoading || isLoadingEnabled,
  }
}

function EulerBorrowOptionPreviewProbe({
  position,
  fallbackBorrowVaultAddress,
  userAddress,
  chainId,
  onResolved,
}: {
  position: MergedPosition
  fallbackBorrowVaultAddress: Address | undefined
  userAddress: Address | undefined
  chainId: number
  onResolved: (preview: WizardEulerPositionPreview) => void
}) {
  const preview = useWizardEulerPositionPreview({
    position,
    fallbackBorrowVaultAddress,
    userAddress,
    chainId,
  })

  useEffect(() => {
    onResolved({
      subAccountId: position.subAccountId,
      spendingPower: preview.spendingPower,
      savingsUSD: preview.savingsUSD,
      hasEnabledCollateral: preview.hasEnabledCollateral,
      hasSavings: preview.hasSavings,
      savingsOnly: preview.savingsOnly,
      isLoading: preview.isLoading,
    })
  }, [
    onResolved,
    position.subAccountId,
    preview.hasEnabledCollateral,
    preview.hasSavings,
    preview.isLoading,
    preview.savingsOnly,
    preview.savingsUSD,
    preview.spendingPower,
  ])

  return null
}

function EulerBorrowSolverOptionButton({
  solverKey,
  title,
  subtitle,
  positions,
  fallbackBorrowVaultAddress,
  userAddress,
  chainId,
  defaultValueLabel,
  defaultDetail,
  onBestPositionResolved,
  selected,
  onClick,
}: {
  solverKey: string
  title: string
  subtitle: string
  positions: MergedPosition[]
  fallbackBorrowVaultAddress: Address | undefined
  userAddress: Address | undefined
  chainId: number
  defaultValueLabel: string
  defaultDetail?: {
    positionCount?: number
    positionLabel?: string
    statusLabel?: string
    metricLabel?: string
    metricValue?: string
    secondaryValue?: string
    helperText?: string
  }
  onBestPositionResolved?: (solverKey: string, subAccountId: number) => void
  selected?: boolean
  onClick: () => void
}) {
  const [previews, setPreviews] = useState<Record<number, WizardEulerPositionPreview>>({})

  const handleResolved = useCallback((preview: WizardEulerPositionPreview) => {
    setPreviews((current) => {
      const existing = current[preview.subAccountId]
      if (
        existing &&
        existing.spendingPower === preview.spendingPower &&
        existing.savingsUSD === preview.savingsUSD &&
        existing.hasEnabledCollateral === preview.hasEnabledCollateral &&
        existing.hasSavings === preview.hasSavings &&
        existing.savingsOnly === preview.savingsOnly &&
        existing.isLoading === preview.isLoading
      ) {
        return current
      }

      return {
        ...current,
        [preview.subAccountId]: preview,
      }
    })
  }, [])

  const bestPreview = useMemo(() => {
    const resolvedPreviews = positions
      .map((position) => ({
        position,
        preview: previews[position.subAccountId],
      }))
      .filter(
        (entry) => entry.preview && !entry.preview.isLoading && entry.preview.spendingPower !== undefined,
      ) as Array<{
      position: MergedPosition
      preview: WizardEulerPositionPreview
    }>

    if (resolvedPreviews.length === 0) return null

    return resolvedPreviews.sort((a, b) => {
      const powerDiff = (b.preview.spendingPower ?? 0) - (a.preview.spendingPower ?? 0)
      if (powerDiff !== 0) return powerDiff

      const aTotal = a.position.collateral.reduce((sum, collateral) => sum + collateral.depositAmountUSD, 0)
      const bTotal = b.position.collateral.reduce((sum, collateral) => sum + collateral.depositAmountUSD, 0)
      return bTotal - aTotal
    })[0]
  }, [positions, previews])

  const detail = bestPreview
    ? {
        positionCount: positions.length,
        positionLabel: `Position ${bestPreview.position.subAccountId}`,
        statusLabel: bestPreview.preview.savingsOnly ? 'Savings only' : undefined,
        metricLabel: 'Spending Power',
        metricValue: formatUsd(bestPreview.preview.spendingPower ?? 0),
        secondaryValue: bestPreview.preview.savingsOnly
          ? `${formatUsd(bestPreview.preview.savingsUSD)} not enabled`
          : bestPreview.preview.hasEnabledCollateral && bestPreview.preview.hasSavings
            ? `+ ${formatUsd(bestPreview.preview.savingsUSD)} in savings`
            : undefined,
        helperText: bestPreview.preview.savingsOnly
          ? 'Select this position to enable your deposits as collateral and unlock spending power.'
          : undefined,
      }
    : defaultDetail

  const valueLabel = bestPreview
    ? `Spending power ${formatUsd(bestPreview.preview.spendingPower ?? 0)}`
    : defaultValueLabel

  useEffect(() => {
    if (!bestPreview || !onBestPositionResolved) return
    onBestPositionResolved(solverKey, bestPreview.position.subAccountId)
  }, [bestPreview, onBestPositionResolved, solverKey])

  return (
    <>
      {positions.map((position) => (
        <EulerBorrowOptionPreviewProbe
          key={position.subAccountId}
          position={position}
          fallbackBorrowVaultAddress={fallbackBorrowVaultAddress}
          userAddress={userAddress}
          chainId={chainId}
          onResolved={handleResolved}
        />
      ))}
      <SolverOptionButton
        title={title}
        subtitle={subtitle}
        valueLabel={valueLabel}
        detail={detail}
        selected={selected}
        onClick={onClick}
      />
    </>
  )
}

function WizardEulerPositionCard({
  position,
  isSelected,
  fallbackBorrowVaultAddress,
  userAddress,
  chainId,
  onClick,
}: {
  position: MergedPosition
  isSelected: boolean
  fallbackBorrowVaultAddress: Address | undefined
  userAddress: Address | undefined
  chainId: number
  onClick: () => void
}) {
  const preview = useWizardEulerPositionPreview({
    position,
    fallbackBorrowVaultAddress,
    userAddress,
    chainId,
  })

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'group relative flex w-full flex-col gap-2 rounded-xl border p-4 text-left transition-all duration-200',
        isSelected
          ? 'border-primary/50 bg-primary/5 shadow-sm ring-1 ring-primary/20'
          : 'border-border/50 bg-secondary/20 hover:border-primary/30 hover:bg-secondary/40',
      )}
    >
      <div className="flex w-full items-center justify-between">
        <div className="flex items-center gap-2">
          {position.debtVaults[0] && (
            <Image
              src={getTokenImage(TokenSymbol(position.debtVaults[0].stableSymbol))}
              alt={position.debtVaults[0].stableSymbol}
              width={22}
              height={22}
              className="rounded-full"
            />
          )}
          <div className="flex flex-col">
            <div className="flex items-center gap-1.5">
              <Typography variant="p" className="font-semibold text-foreground text-sm leading-tight tracking-tight">
                {position.debtVaults[0]?.stableSymbol ?? `Position ${position.subAccountId}`}
              </Typography>
              {preview.savingsOnly && !preview.isLoading && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="flex items-center gap-1 rounded-full bg-amber-500/10 px-1.5 py-0.5 font-semibold text-[9px] text-amber-400 uppercase tracking-wide">
                      Savings only
                    </span>
                  </TooltipTrigger>
                  <TooltipContentShort>
                    You&apos;ve deposited here but no vault is enabled as collateral. Select this position to unlock
                    spending power.
                  </TooltipContentShort>
                </Tooltip>
              )}
            </div>
            <span className="text-muted-foreground text-xs leading-tight">Position {position.subAccountId}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="text-right">
            <div className="text-muted-foreground text-xs">Spending Power</div>
            <div className="font-semibold text-foreground text-sm">
              {preview.isLoading || preview.spendingPower === undefined ? (
                <span className="animate-pulse text-muted-foreground">...</span>
              ) : (
                formatUsd(preview.spendingPower)
              )}
            </div>
            {preview.hasEnabledCollateral && preview.hasSavings && (
              <div className="text-[10px] text-muted-foreground/70 leading-tight">
                + {formatUsd(preview.savingsUSD)} in savings
              </div>
            )}
            {preview.savingsOnly && !preview.isLoading && (
              <div className="text-[10px] text-amber-400/80 leading-tight">
                {formatUsd(preview.savingsUSD)} not enabled
              </div>
            )}
          </div>
          {isSelected && (
            <div className="flex h-5 w-5 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-sm">
              <Check className="h-3 w-3 stroke-[3]" />
            </div>
          )}
        </div>
      </div>

      {preview.savingsOnly && !preview.isLoading && (
        <div className="mt-1 flex items-center gap-2 rounded-md border border-amber-500/20 bg-amber-500/10 px-3 py-2">
          <Info className="h-3.5 w-3.5 shrink-0 text-amber-400" />
          <Typography variant="p" className="font-medium text-amber-300 text-xs">
            Select this position to enable your deposits as collateral and unlock spending power.
          </Typography>
        </div>
      )}

      {position.hasUnsupportedDebt && (
        <div className="mt-1 rounded-md border border-yellow-500/20 bg-yellow-500/10 px-3 py-2">
          <Typography variant="p" className="font-medium text-xs text-yellow-500">
            Has unsupported debt vault
          </Typography>
        </div>
      )}
    </button>
  )
}

function WizardModeToggle({
  mode,
  onChange,
  canBorrow,
  canSpend,
}: {
  mode: 'borrow' | 'spend'
  onChange: (mode: 'borrow' | 'spend') => void
  canBorrow: boolean
  canSpend: boolean
}) {
  return (
    <div className="relative flex rounded-lg border border-border bg-muted/30 p-0.5">
      <div
        className="absolute top-0.5 bottom-0.5 rounded-md bg-primary/15 transition-all duration-200"
        style={{
          left: mode === 'borrow' ? '2px' : '50%',
          width: 'calc(50% - 4px)',
        }}
      />
      <button
        type="button"
        onClick={() => onChange('borrow')}
        disabled={!canBorrow}
        className={cn(
          'relative z-10 flex-1 rounded-md px-4 py-1.5 text-center font-medium text-sm transition-colors',
          mode === 'borrow' ? 'text-primary' : 'text-muted-foreground hover:text-foreground',
          !canBorrow && 'cursor-not-allowed opacity-50',
        )}
      >
        Borrow
      </button>
      <button
        type="button"
        onClick={() => onChange('spend')}
        disabled={!canSpend}
        className={cn(
          'relative z-10 flex-1 rounded-md px-4 py-1.5 text-center font-medium text-sm transition-colors',
          mode === 'spend' ? 'text-primary' : 'text-muted-foreground hover:text-foreground',
          !canSpend && 'cursor-not-allowed opacity-50',
        )}
      >
        Spend
      </button>
    </div>
  )
}

function SolverOptionButton({
  title,
  subtitle,
  valueLabel,
  detail,
  selected = false,
  onClick,
  disabled = false,
}: {
  title: string
  subtitle: string
  valueLabel: string
  detail?: {
    positionCount?: number
    positionLabel?: string
    statusLabel?: string
    metricLabel?: string
    metricValue?: string
    secondaryValue?: string
    helperText?: string
  }
  selected?: boolean
  onClick: () => void
  disabled?: boolean
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'flex h-full flex-col items-start justify-between gap-4 rounded-xl border p-4 text-left transition-colors',
        selected ? 'border-primary bg-primary/10 shadow-sm' : 'border-border bg-background',
        disabled ? 'cursor-not-allowed opacity-60' : 'hover:border-primary hover:bg-primary/5',
      )}
    >
      <div className="w-full">
        <Typography variant="p" className="font-medium text-sm">
          {title}
        </Typography>
        <Typography variant="p" className="text-muted-foreground text-xs">
          {subtitle}
        </Typography>
        {detail && (
          <div className="mt-2 flex flex-col gap-1.5">
            <div className="flex flex-wrap items-center gap-2">
              {detail.positionCount != null && detail.positionCount > 0 && (
                <span className="rounded bg-emerald-500/10 px-1.5 py-0.5 font-medium text-[10px] text-emerald-400">
                  {detail.positionCount} Position{detail.positionCount === 1 ? '' : 's'}
                </span>
              )}
              {detail.statusLabel && (
                <span className="rounded bg-amber-500/10 px-1.5 py-0.5 font-medium text-[10px] text-amber-400 uppercase">
                  {detail.statusLabel}
                </span>
              )}
            </div>
            {detail.positionLabel && (
              <Typography variant="p" className="text-muted-foreground text-xs">
                {detail.positionLabel}
              </Typography>
            )}
            {(detail.metricLabel || detail.metricValue) && (
              <div className="flex items-end gap-2">
                {detail.metricLabel && (
                  <Typography variant="p" className="text-muted-foreground text-xs">
                    {detail.metricLabel}
                  </Typography>
                )}
                {detail.metricValue && (
                  <Typography variant="p" className="font-medium text-xs">
                    {detail.metricValue}
                  </Typography>
                )}
              </div>
            )}
            {detail.secondaryValue && (
              <Typography variant="p" className="text-[10px] text-amber-400/80">
                {detail.secondaryValue}
              </Typography>
            )}
            {detail.helperText && (
              <Typography variant="p" className="text-[10px] text-amber-300 leading-tight">
                {detail.helperText}
              </Typography>
            )}
          </div>
        )}
      </div>
      <div className="flex w-full items-center justify-between gap-3">
        <Typography variant="p" className="text-right text-muted-foreground text-xs">
          {valueLabel}
        </Typography>
        <ChevronRight className="h-4 w-4 text-muted-foreground" />
      </div>
    </button>
  )
}

function formatSolverOptionValue(value: number | undefined, isLoading: boolean): string {
  if (isLoading) return 'Loading...'

  return `Spending power ${formatUsd(value ?? 0)}`
}

function formatUsd(value: number): string {
  return `$${value.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`
}

function formatApy(value: number): string {
  return `${value.toFixed(2)}%`
}

function ReviewRow({ label, value, muted }: { label: string; value: string; muted?: boolean }) {
  return (
    <div className="flex items-center justify-between">
      <Typography variant="p" className="text-muted-foreground text-xs">
        {label}
      </Typography>
      <Typography variant="p" className={cn('text-sm', muted ? 'text-muted-foreground italic' : 'font-medium')}>
        {value}
      </Typography>
    </div>
  )
}
